import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Mic, Square, User, Bot, Sparkles, Volume2 } from 'lucide-react';

// ── Personas (drives greeting, voice and reply style) ───────────────────────────
const PERSONAS = [
  { id: 'telco', name: 'Telco', persona: 'Aria', from: '#e60000', to: '#ff6a3d', g: 'female',
    greet: "Hi, I'm Aria, the Vokentra Telco agent. Ask me about your bill, signal, SIM, roaming or an upgrade." },
  { id: 'care', name: 'Care', persona: 'Caira', from: '#10b981', to: '#22d3ee', g: 'female',
    greet: "Hello, I'm Caira from Vokentra Care. I can help with appointments or a prescription question." },
  { id: 'travel', name: 'Travel', persona: 'Voya', from: '#3b82f6', to: '#8b5cf6', g: 'male',
    greet: "Hi, I'm Voya, your Vokentra Travel agent. Ask me about a booking, a change, or data abroad." },
  { id: 'finance', name: 'Finance', persona: 'Fynn', from: '#f59e0b', to: '#eab308', g: 'male',
    greet: "Hi, I'm Fynn from Vokentra Finance. I can help with a transaction, a card, or a dispute — securely." },
];

type Mode = 'idle' | 'greeting' | 'listening' | 'thinking' | 'speaking';
const SR: any = (typeof window !== 'undefined' && ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)) || null;

// Browser-only, telecom-aware demo responses (no backend / no login needed).
function reply(text: string, personaId: string): string {
  const m = text.toLowerCase();
  if (personaId === 'care') return "I can help with that. I'll check the clinic's diary and find you the earliest suitable slot — would mornings or afternoons suit you better?";
  if (personaId === 'travel') return "Let me look into that for you. I can change your booking or add a travel data pass so you're covered abroad — where are you heading and when?";
  if (personaId === 'finance') return "Thanks — for your security I'll verify a couple of details first, then I can check that transaction, freeze the card, or open a dispute. Which would you like?";
  // Telco
  if (/bill|charge|refund|overcharg|expensive|cost/.test(m)) return "I can help with your bill. Bills often jump when a promotional discount ends — I'll check your account and look at current offers to bring it back down. Shall I do that?";
  if (/signal|reception|no service|drop|coverage|5g|4g/.test(m)) return "Sorry about the signal trouble. I'll check for outages in your area and run a line test, then I can switch on Wi-Fi Calling so your calls use broadband. Want me to start?";
  if (/internet|broadband|wi-?fi|slow/.test(m)) return "Let me look into your broadband. I'll run a line check, raise a fault to our network team if needed, and text you updates. Shall I begin?";
  if (/sim|esim/.test(m)) return "I can help with your SIM. If it isn't registering I'll order a free replacement, or switch you to an eSIM right now — which would you prefer?";
  if (/roam|abroad|travel|spain|france|europe|holiday/.test(m)) return "Travelling soon? I'll check your roaming and add a travel pass so your data works abroad without any bill shock. Where are you headed?";
  if (/upgrade|more data|unlimited|plan/.test(m)) return "Happy to help you upgrade. Unlimited Plus is twenty-five pounds a month with 5G and EU roaming — a great fit for most people. Would you like me to switch you to that?";
  if (/cancel|leave|switch|porting/.test(m)) return "I'd be sorry to see you go. Before you decide, I can offer a loyalty discount or a better-value plan — may I show you those?";
  if (/fraud|hack|stolen|swap|scam/.test(m)) return "That's important. For your security I'll place a protection hold on your account and connect you to our fraud team straight away — I'm treating this as urgent.";
  if (/hello|hi |hey|help|there/.test(m)) return "Hi! I'm the Vokentra Telco agent. You can ask about your bill, signal, SIM, roaming or upgrades — what's on your mind?";
  return "Got it. As the Vokentra Telco agent I can help with billing, network, SIM, roaming and upgrades — tell me a little more about what you need.";
}

export function AgentRuntime() {
  const [pid, setPid] = useState('telco');
  const persona = PERSONAS.find((p) => p.id === pid)!;
  const [mode, setMode] = useState<Mode>('idle');
  const [partial, setPartial] = useState('');
  const [turns, setTurns] = useState<{ who: 'you' | 'agent'; text: string }[]>([]);
  const [level, setLevel] = useState(0); // live mic amplitude 0..1
  const [err, setErr] = useState('');

  const recogRef = useRef<any>(null);
  const acRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const activeRef = useRef(false);
  const modeRef = useRef<Mode>('idle');
  const pidRef = useRef(pid);
  const scrollRef = useRef<HTMLDivElement>(null);

  const setM = (m: Mode) => { modeRef.current = m; setMode(m); };
  useEffect(() => { pidRef.current = pid; }, [pid]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: 'smooth' }); }, [turns, partial]);
  useEffect(() => () => stop(), []); // cleanup on unmount

  function pickVoice(): SpeechSynthesisVoice | null {
    try {
      const vs = window.speechSynthesis.getVoices();
      const wantMale = persona.g === 'male';
      const byLang = vs.filter((v) => /en[-_]?(GB|US)/i.test(v.lang));
      const pool = byLang.length ? byLang : vs;
      const match = pool.find((v) => (wantMale ? /male|daniel|alex|george|fred/i : /female|samantha|kate|serena|fiona|victoria/i).test(v.name));
      return match || pool[0] || null;
    } catch { return null; }
  }

  function speak(text: string, after?: () => void) {
    setTurns((t) => [...t, { who: 'agent', text }]);
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = pickVoice(); if (v) u.voice = v;
      u.lang = 'en-GB'; u.rate = 1.02; u.pitch = persona.g === 'male' ? 0.95 : 1.05;
      setM('speaking');
      u.onend = () => { if (activeRef.current) (after ? after() : listen()); };
      u.onerror = () => { if (activeRef.current) (after ? after() : listen()); };
      window.speechSynthesis.speak(u);
      // safety timeout in case onend never fires
      setTimeout(() => { if (activeRef.current && modeRef.current === 'speaking') (after ? after() : listen()); }, 1200 + text.length * 60);
    } catch { if (activeRef.current) (after ? after() : listen()); }
  }

  function listen() {
    if (!activeRef.current || !SR) return;
    setM('listening');
    try { recogRef.current?.abort?.(); } catch { /* */ }
    const r = new SR();
    r.lang = 'en-GB'; r.interimResults = true; r.continuous = false; r.maxAlternatives = 1;
    r.onresult = (e: any) => {
      let interim = '', final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t; else interim += t;
      }
      setPartial(interim);
      if (final.trim()) {
        setPartial('');
        setTurns((t) => [...t, { who: 'you', text: final.trim() }]);
        setM('thinking');
        try { r.stop(); } catch { /* */ }
        setTimeout(() => speak(reply(final.trim(), pidRef.current)), 500);
      }
    };
    r.onend = () => { if (activeRef.current && modeRef.current === 'listening') { try { r.start(); } catch { /* */ } } };
    r.onerror = (ev: any) => { if (ev?.error === 'not-allowed') { setErr('Microphone blocked — allow mic access to talk.'); stop(); } };
    recogRef.current = r;
    try { r.start(); } catch { /* */ }
  }

  async function start() {
    setErr('');
    activeRef.current = true;
    setTurns([]); setPartial('');
    // Mic amplitude → reactive orb (real user voice drives the visual)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ac: AudioContext = new AC(); acRef.current = ac;
      const src = ac.createMediaStreamSource(stream);
      const an = ac.createAnalyser(); an.fftSize = 512; src.connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const loop = () => {
        an.getByteTimeDomainData(buf);
        let sum = 0; for (let i = 0; i < buf.length; i++) { const d = (buf[i] - 128) / 128; sum += d * d; }
        const rms = Math.sqrt(sum / buf.length);
        setLevel((prev) => prev * 0.6 + Math.min(1, rms * 3) * 0.4);
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch { setErr('Microphone not available — you can still hear the agent.'); }
    // Greet, then start listening
    setM('greeting');
    speak(persona.greet);
  }

  function stop() {
    activeRef.current = false;
    setM('idle'); setPartial(''); setLevel(0);
    try { recogRef.current?.abort?.(); } catch { /* */ }
    try { window.speechSynthesis.cancel(); } catch { /* */ }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
    try { acRef.current?.close(); } catch { /* */ }
    streamRef.current = null; acRef.current = null;
  }

  const active = mode !== 'idle';
  const status = mode === 'greeting' || mode === 'speaking' ? `${persona.persona} is speaking…`
    : mode === 'listening' ? 'Listening… speak now' : mode === 'thinking' ? 'Thinking…'
    : 'Tap to start a live conversation';
  const orbScale = mode === 'listening' ? 1 + level * 0.5
    : mode === 'speaking' || mode === 'greeting' ? 1.12 : 1;

  return (
    <section className="px-6 py-20 max-w-6xl mx-auto">
      <div className="text-center mb-10">
        <div className="text-xs font-bold tracking-[0.2em] text-text-muted inline-flex items-center gap-2 justify-center">
          <Sparkles className="w-3.5 h-3.5 text-vokentra" /> LIVE AGENT
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold mt-3">Don’t read about it — <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(120deg,#ff6a3d,#e60000 50%,#a855f7)' }}>talk to it</span></h2>
        <p className="text-text-secondary mt-4 max-w-2xl mx-auto">
          A real Vokentra voice agent, right here in your browser. Pick an agent, press talk, and have a
          conversation — it listens to your microphone, understands, and answers in a synthetic voice.
        </p>
      </div>

      {/* Persona selector */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-8">
        {PERSONAS.map((p) => {
          const on = p.id === pid;
          return (
            <button key={p.id} onClick={() => { setPid(p.id); if (active) stop(); }}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${on ? 'text-white border-transparent' : 'text-text-muted border-white/[0.1] hover:text-text-secondary'}`}
              style={on ? { background: `linear-gradient(135deg, ${p.from}, ${p.to})` } : {}}>
              {p.name} <span className="opacity-70">· {p.persona}</span>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6 items-stretch">
        {/* Reactive voice stage */}
        <div className="relative rounded-3xl border border-white/[0.08] bg-white/[0.02] p-8 flex flex-col items-center justify-center min-h-[420px] overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse 70% 60% at 50% 40%, ${persona.to}1f, transparent 70%)` }} />

          {/* concentric reactive rings */}
          <div className="relative flex items-center justify-center my-4" style={{ width: 240, height: 240 }}>
            {active && [0, 1, 2].map((k) => (
              <motion.span key={k} className="absolute rounded-full border" style={{ borderColor: persona.to }}
                initial={{ width: 120, height: 120, opacity: 0.5 }}
                animate={{ width: [120, 240], height: [120, 240], opacity: [0.5, 0] }}
                transition={{ duration: 2.2, repeat: Infinity, delay: k * 0.7, ease: 'easeOut' }} />
            ))}
            {/* orb */}
            <motion.div className="relative rounded-full flex items-center justify-center shadow-2xl"
              animate={{ scale: orbScale }} transition={{ type: 'spring', stiffness: 260, damping: 18 }}
              style={{ width: 150, height: 150, background: `radial-gradient(circle at 35% 30%, ${persona.from}, ${persona.to})`,
                       boxShadow: `0 0 60px ${persona.to}66` }}>
              {/* inner voice bars */}
              <div className="flex items-end gap-1 h-12">
                {Array.from({ length: 7 }).map((_, k) => (
                  <motion.span key={k} className="w-1.5 rounded-full bg-white/90"
                    animate={active ? { height: [8, 10 + (mode === 'listening' ? level * 36 : 26) - Math.abs(3 - k) * 4, 8] } : { height: 8 }}
                    transition={active ? { duration: 0.5 + k * 0.06, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.3 }} />
                ))}
              </div>
            </motion.div>
          </div>

          <p className="text-sm text-text-secondary h-5 mb-1">{status}</p>
          {partial && <p className="text-xs text-text-muted italic mb-1">“{partial}”</p>}
          {err && <p className="text-xs text-warning mb-1">{err}</p>}

          <div className="mt-4">
            {!active ? (
              <button onClick={start}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold shadow-xl transition-transform hover:scale-105"
                style={{ background: `linear-gradient(135deg, ${persona.from}, ${persona.to})`, boxShadow: `0 10px 30px ${persona.to}55` }}>
                <Mic className="w-4 h-4" /> Talk to {persona.persona}
              </button>
            ) : (
              <button onClick={stop}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full border-2 border-white/30 text-text-primary font-semibold hover:bg-white/[0.05] transition-colors">
                <Square className="w-4 h-4" /> End conversation
              </button>
            )}
          </div>
          <p className="text-[10px] text-text-muted mt-4 text-center max-w-[18rem]">
            {SR ? 'Two-way voice runs in your browser — needs mic permission. Best in Chrome/Edge.' : 'Your browser can’t capture speech; the agent will still speak. Try Chrome/Edge.'}
          </p>
        </div>

        {/* Live transcript */}
        <div className="rounded-3xl border border-white/[0.08] bg-white/[0.02] p-5 flex flex-col min-h-[420px]">
          <div className="flex items-center gap-2 text-[11px] font-bold tracking-wider text-text-muted mb-3">
            <Volume2 className="w-3.5 h-3.5" /> LIVE TRANSCRIPT
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pr-1">
            {turns.length === 0 && !partial && (
              <div className="h-full flex flex-col items-center justify-center text-center text-text-muted text-sm gap-2">
                <Mic className="w-6 h-6 opacity-40" />
                <p>Press “Talk to {persona.persona}”, allow your microphone,<br />then say something like <span className="text-text-secondary">“my bill is higher than usual”</span>.</p>
              </div>
            )}
            {turns.map((t, i) => (
              <div key={i} className={`flex gap-2 ${t.who === 'you' ? 'justify-end' : ''}`}>
                {t.who === 'agent' && <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: `linear-gradient(135deg, ${persona.from}, ${persona.to})` }}><Bot className="w-4 h-4" /></div>}
                <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${t.who === 'you' ? 'bg-vokentra/15 text-text-primary' : 'bg-white/[0.04] text-text-secondary'}`}>{t.text}</div>
                {t.who === 'you' && <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center text-text-muted shrink-0"><User className="w-4 h-4" /></div>}
              </div>
            ))}
            {partial && (
              <div className="flex gap-2 justify-end opacity-60">
                <div className="max-w-[78%] rounded-2xl px-3 py-2 text-sm bg-vokentra/10 text-text-primary italic">{partial}</div>
                <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center text-text-muted shrink-0"><User className="w-4 h-4" /></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default AgentRuntime;
